import {
    collection,
    doc,
    writeBatch,
    updateDoc,
    deleteDoc,
    getDocs,
    query,
    where,
    getDoc,
    setDoc,
    deleteField,
} from 'firebase/firestore'
import type { BankTransaction, UserBankAccount } from '~/storage/types'
import { StorageModuleDeps } from '~common/storage/types'
import { collectionRef, docRef, fillDoc } from '~common/utils/firestore-helpers'

export class BankTransactionStorageModule {
    constructor(private readonly deps: StorageModuleDeps) {}

    /**
     * Saves a batch of transactions to Firestore.
     *
     * @param transactions - The transactions to save (up to 500 due to Firestore batch limits).
     * @returns The IDs of the saved transactions.
     * @throws Error if more than 500 transactions are provided, as Firestore batches are limited to 500 operations.
     */
    async saveTransactions(
        transactions: Omit<BankTransaction, 'id'>[],
    ): Promise<{ ids: string[] }> {
        const userId = this.deps.ensureAuthenticated()
        const batch = writeBatch(this.deps.db)
        const transactionsCol = collection(
            this.deps.db,
            'users',
            userId,
            'transactions',
        )

        const ids: string[] = []
        transactions.forEach((transaction) => {
            const docRef = doc(transactionsCol)
            ids.push(docRef.id)
            batch.set(docRef, transaction)
        })

        await batch.commit()
        return { ids }
    }

    async update(
        id: string,
        updates: Partial<
            Omit<BankTransaction, 'id' | 'createdAt' | 'updatedAt'>
        >,
        now: number = Date.now(),
    ): Promise<void> {
        const userId = this.deps.ensureAuthenticated()
        const docRef = doc(this.deps.db, 'users', userId, 'transactions', id)

        const firestoreUpdates: Record<string, unknown> = { updatedAt: now }
        for (const [key, value] of Object.entries(updates)) {
            if (value === undefined) {
                firestoreUpdates[key] = deleteField()
            } else {
                firestoreUpdates[key] = value
            }
        }

        await updateDoc(docRef, firestoreUpdates)
    }

    async delete(id: string): Promise<void> {
        const userId = this.deps.ensureAuthenticated()
        const docRef = doc(this.deps.db, 'users', userId, 'transactions', id)
        await deleteDoc(docRef)
    }

    async getAllTransactions(): Promise<BankTransaction[]> {
        const userId = this.deps.ensureAuthenticated()
        const transactionsRef = collectionRef<BankTransaction>(
            this.deps.db,
            'users',
            userId,
            'transactions',
        )
        const snapshot = await getDocs(transactionsRef)
        return snapshot.docs.map(fillDoc)
    }

    async getTransactionById(id: string): Promise<BankTransaction | null> {
        const userId = this.deps.ensureAuthenticated()
        const transactionRef = docRef<BankTransaction>(
            this.deps.db,
            'users',
            userId,
            'transactions',
            id,
        )
        const docSnap = await getDoc(transactionRef)
        if (docSnap.exists()) {
            return fillDoc(docSnap)
        }
        return null
    }

    async getAllBankAccounts(): Promise<UserBankAccount[]> {
        const userId = this.deps.ensureAuthenticated()
        const bankAccountsRef = collectionRef<UserBankAccount>(
            this.deps.db,
            'users',
            userId,
            'bankAccounts',
        )
        const snapshot = await getDocs(bankAccountsRef)
        return snapshot.docs.map((doc) => doc.data())
    }

    async saveBankAccount(
        account: Omit<UserBankAccount, 'createdAt' | 'updatedAt'>,
        now: number = Date.now(),
    ): Promise<void> {
        const userId = this.deps.ensureAuthenticated()
        const accountData = {
            ...account,
            createdAt: now,
            updatedAt: now,
        }

        const docRef = doc(
            this.deps.db,
            'users',
            userId,
            'bankAccounts',
            account.iban,
        )

        await setDoc(docRef, accountData, { merge: true })
    }

    async deleteBankAccount(id: string): Promise<void> {
        const userId = this.deps.ensureAuthenticated()
        const docRef = doc(this.deps.db, 'users', userId, 'bankAccounts', id)
        await deleteDoc(docRef)
    }
}
